import FullStoryApi from "@claroty/utilities/tracking/fullStoryUtils";
import { PERFORMANCE_ACTIONS } from "@claroty/utilities/tracking/trackingConsts";
import { isAnalyticsDisabled } from "../isAnalyticsDisabled";
import { PageLoadMetrics } from "./types";

/**
 * FullStory event structure for page load performance metrics
 * Uses FullStory type suffixes: _str for strings, _int for integers, _real for floats
 */
interface PageLoadFullStoryEvent {
    // Standard fields
    routePath_str: string;
    name_str: string;
    duration_real: number;

    // Request metrics
    requestCount_int: number;
    successfulRequests_int: number;
    failedRequests_int: number;
    maxConcurrentRequests_int: number;

    // Duration metrics
    totalLoadDuration_real: number;
    sessionDuration_real: number;

    // Optional metrics
    slowestRequestUrl_str?: string;
    slowestRequestDuration_real?: number;
    averageRequestDuration_real?: number;
}

/**
 * Sends page load performance metrics to FullStory
 *
 * Formats the metrics with FullStory type suffixes and sends them as a custom event.
 * This allows page load performance data to be visible in FullStory session replays.
 *
 * @param metrics - Page load metrics to send
 * @param routeTrackingName - Optional tracking name for the route
 * @param routePath - Route path
 * @param duration - Total duration from timer (in seconds)
 */
export function sendPageLoadToFullStory(
    metrics: PageLoadMetrics,
    routeTrackingName: string | undefined,
    routePath: string,
    duration: number,
): void {
    // Don't send if analytics is disabled
    if (isAnalyticsDisabled()) {
        return;
    }

    // Don't send if duration is zero (no meaningful loading time)
    if (duration === 0) {
        return;
    }

    try {
        // Build the event object with FullStory type suffixes
        const fullStoryEvent: PageLoadFullStoryEvent = {
            // Standard fields
            routePath_str: routePath,
            name_str: routeTrackingName || routePath,
            duration_real: duration,

            // Request metrics
            requestCount_int: metrics.requestCount,
            successfulRequests_int: metrics.successfulRequests,
            failedRequests_int: metrics.failedRequests,
            maxConcurrentRequests_int: metrics.maxConcurrentRequests,

            // Duration metrics
            totalLoadDuration_real: metrics.totalDuration,
            sessionDuration_real: metrics.sessionDuration,
        };

        // Add optional metrics if they exist
        if (metrics.slowestRequestUrl !== undefined) {
            fullStoryEvent.slowestRequestUrl_str = metrics.slowestRequestUrl;
        }
        if (metrics.slowestRequestDuration !== undefined) {
            fullStoryEvent.slowestRequestDuration_real = metrics.slowestRequestDuration;
        }
        if (metrics.averageRequestDuration !== undefined) {
            fullStoryEvent.averageRequestDuration_real = metrics.averageRequestDuration;
        }

        // Send the event to FullStory
        FullStoryApi.event(PERFORMANCE_ACTIONS.PAGE_LOAD_DURATION, fullStoryEvent);
    } catch (error) {
        // Silently fail - don't break the app if FullStory is not available
        // This matches the pattern used in other FullStory integrations
    }
}
